import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminPaymentAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings, Upload, Plus, X, DollarSign, Building2, QrCode, Smartphone, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPaymentSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newUpi, setNewUpi] = useState('');

  const load = async () => {
    try {
      const res = await adminPaymentAPI.get();
      setSettings(res.data);
    } catch { toast.error('Failed to load settings'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminPaymentAPI.update(settings);
      toast.success('Payment settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const addUpi = () => {
    if (!newUpi.trim()) return;
    setSettings({ ...settings, upi_ids: [...(settings.upi_ids || []), newUpi.trim()] });
    setNewUpi('');
  };

  const removeUpi = (idx) => {
    const updated = [...(settings.upi_ids || [])];
    updated.splice(idx, 1);
    setSettings({ ...settings, upi_ids: updated });
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await adminPaymentAPI.uploadQR(fd);
      setSettings({ ...settings, qr_code_url: res.data.qr_code_url });
      toast.success('QR Code uploaded');
    } catch { toast.error('Failed to upload QR Code'); }
  };

  if (loading) return <AdminLayout><div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div></AdminLayout>;

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-payment-settings">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
            <Settings className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Payment Settings</h1>
            <p className="text-xs text-[var(--text-secondary)]">Configure payment methods displayed to users</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* USD to INR Rate */}
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="rate-settings">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Exchange Rate</h2>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[var(--text-secondary)]">USD to INR Rate</Label>
              <Input type="number" step="0.01" value={settings?.usd_to_inr_rate || ''} data-testid="rate-input"
                onChange={(e) => setSettings({ ...settings, usd_to_inr_rate: parseFloat(e.target.value) || 0 })}
                className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono h-10" />
              <p className="text-[10px] text-[var(--text-tertiary)]">1 USD = ₹{settings?.usd_to_inr_rate} INR</p>
            </div>
          </div>

          {/* QR Code */}
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="qr-settings">
            <div className="flex items-center gap-2">
              <QrCode className="w-4 h-4 text-green-400" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">QR Code</h2>
            </div>
            {settings?.qr_code_url && (
              <div className="flex justify-center p-3 bg-white rounded">
                <img src={API_BASE + settings.qr_code_url} alt="QR Code" className="w-32 h-32 object-contain" />
              </div>
            )}
            <label className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-primary)] border border-dashed border-[var(--border-subtle)] rounded cursor-pointer hover:border-green-500/40 transition-colors" data-testid="qr-upload">
              <Upload className="w-5 h-5 text-[var(--text-tertiary)]" />
              <span className="text-sm text-[var(--text-secondary)]">{settings?.qr_code_url ? 'Replace QR Code' : 'Upload QR Code'}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleQRUpload} />
            </label>
          </div>

          {/* UPI IDs */}
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="upi-settings">
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-green-400" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">UPI IDs</h2>
            </div>
            <div className="space-y-2">
              {(settings?.upi_ids || []).map((upi, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <span className="text-sm font-mono text-[var(--text-primary)]">{upi}</span>
                  <button onClick={() => removeUpi(i)} className="p-1 text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input type="text" value={newUpi} onChange={(e) => setNewUpi(e.target.value)} placeholder="new-upi@provider" data-testid="new-upi-input"
                onKeyDown={(e) => e.key === 'Enter' && addUpi()}
                className="flex-1 bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              <button onClick={addUpi} className="px-3 py-2 bg-green-500/15 text-green-400 rounded hover:bg-green-500/25">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="bank-settings">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-green-400" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Bank Details</h2>
            </div>
            {[
              { key: 'account_holder', label: 'Account Holder Name' },
              { key: 'bank_name', label: 'Bank Name' },
              { key: 'account_number', label: 'Account Number' },
              { key: 'ifsc_code', label: 'IFSC Code' },
            ].map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">{field.label}</Label>
                <Input type="text" value={settings?.[field.key] || ''} data-testid={`bank-${field.key}`}
                  onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              </div>
            ))}
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} data-testid="save-payment-settings"
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-40">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>
    </AdminLayout>
  );
}
